export type InstallPlatform = "ios" | "other";
export type InstallSnapshot = { installed: boolean; nativePromptAvailable: boolean; platform: InstallPlatform };

type InstallChoice = { outcome: "accepted" | "dismissed"; platform: string };
interface DeferredInstallPrompt extends Event { prompt(): Promise<void>; userChoice: Promise<InstallChoice> }

export const INSTALL_DISMISS_DAYS = 7;
export const detectInstallPlatform = (userAgent: string, touchPoints: number): InstallPlatform =>
  /iphone|ipad|ipod/i.test(userAgent) || (/macintosh/i.test(userAgent) && touchPoints > 1) ? "ios" : "other";

export const shouldOfferInstall = (input: { hasFirstValue: boolean; installed: boolean; dismissedUntil: number; now: number }): boolean =>
  input.hasFirstValue && !input.installed && input.dismissedUntil <= input.now;

const runningStandalone = () => typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone));

let deferredPrompt: DeferredInstallPrompt | null = null;
let installed = runningStandalone();
const listeners = new Set<(snapshot: InstallSnapshot) => void>();

export const getInstallSnapshot = (): InstallSnapshot => ({
  installed,
  nativePromptAvailable: deferredPrompt !== null,
  platform: typeof navigator === "undefined" ? "other" : detectInstallPlatform(navigator.userAgent, navigator.maxTouchPoints)
});

const publish = () => { const snapshot = getInstallSnapshot(); listeners.forEach((listener) => listener(snapshot)); };

export const subscribeInstall = (listener: (snapshot: InstallSnapshot) => void): (() => void) => {
  listeners.add(listener); listener(getInstallSnapshot()); return () => listeners.delete(listener);
};

export const promptPwaInstall = async (): Promise<"accepted" | "dismissed" | "unavailable"> => {
  const prompt = deferredPrompt;
  if (!prompt) return "unavailable";
  deferredPrompt = null; publish();
  await prompt.prompt();
  return (await prompt.userChoice).outcome;
};

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault(); deferredPrompt = event as DeferredInstallPrompt; publish();
  });
  window.addEventListener("appinstalled", () => { deferredPrompt = null; installed = true; publish(); });
  window.matchMedia("(display-mode: standalone)").addEventListener?.("change", (event) => {
    if (event.matches) { installed = true; deferredPrompt = null; publish(); }
  });
}
